import Client from '../../Client';
import Occupier from '../../Entities/Buildings/Occupier';
import OccupierIndustry from '../../Entities/Buildings/Occupiers/OccupierIndustry';
import GenericNamespaceHandler from '../../GenericNamespaceHandler';

export default class OccupiersNamespace extends GenericNamespaceHandler {
  namespace = 'occupiers';

  searchCall = '';

  createCall = 'create';

  updateCall = 'update';

  deleteCall = 'delete';

  searchRequiresBuildingId = true;

  responseEntity = Occupier;

  responseKey = 'occupier';

  requiredFields = [
    {
      param: 'building_id',
      requiredForCreate: true,
      requiredForUpdate: false,
      error: 'provide_building_id',
      validation: { type: 'int' }
    },
    {
      param: 'name',
      requiredForCreate: false,
      requiredForUpdate: false,
      error: 'invalid_name',
      validation: { type: 'string', min: 5, max: 255 }
    },
    {
      param: 'phone',
      requiredForCreate: false,
      requiredForUpdate: false,
      error: 'invalid_phone',
      validation: { type: 'string', min: 5, max: 255 }
    },
    {
      param: 'email',
      requiredForCreate: false,
      requiredForUpdate: false,
      error: 'invalid_email',
      validation: { type: 'email' }
    }
  ];

  /** *
   * @param {Client} Client - instance of client
   */
  constructor(Client) {
    super(Client);
  }

  searchByBuildingId(
    buildingId,
    criteria = {},
    page = 1,
    orderBy,
    orderDirection
  ) {
    if (buildingId) criteria.building_id = buildingId;
    return this.search(criteria, page, orderBy, orderDirection);
  }

  create(
    buildingId = null,
    companyId = null,
    floorsIds = [],
    isRetailer = false,
    isNeighbour = false,
    optional = {
      name: undefined,
      phone: undefined,
      email: undefined,
      contact_job_role: undefined,
      description: undefined,
      images: undefined,
      industry_id: undefined,
      public_phone: undefined,
      public_website: undefined,
      hours: undefined,
      contacts: undefined,
      domains: undefined
    }
  ) {
    const data = {
      building_id: buildingId,
      company_id: companyId,
      floor_ids: floorsIds,
      enabled: true,
      neighbour: isNeighbour,
      retailer: isRetailer,
      ...optional
    };
    return super.create(data);
  }

  update(
    id = null,
    optional = {
      retailer: undefined,
      occupier: undefined,
      floor_ids: undefined,
      name: undefined,
      phone: undefined,
      email: undefined,
      contact_job_role: undefined,
      description: undefined,
      images: undefined,
      images_for_delete: undefined,
      industry_id: undefined,
      public_phone: undefined,
      public_website: undefined,
      hours: undefined,
      contacts: undefined,
      domains: undefined
    }
  ) {
    return super.update(id, optional);
  }

  searchIndustriesByBuildingId(buildingId) {
    return new Promise((resolve, reject) => {
      this.client.request(
        this.namespace,
        'industries',
        (error, { data }) => {
          let { page, pages, records } = data;

          let results = [];
          if (!page) page = 1;
          if (!pages) pages = 1;

          if (Array.isArray(records))
            results = records.map(
              (entity) => new OccupierIndustry(this.client, entity)
            );
          resolve({ results, page, pages });
        },
        { building_id: buildingId },
        Client.methods.GET
      );
    });
  }

  archive(id) {
    return this.toggleArchived(id);
  }

  unarchive(id) {
    return this.toggleArchived(id, true);
  }

  toggleArchived(id, unarchive) {
    return new Promise((resolve, reject) => {
      if (!id) throw new Error('provide_id');

      this.client.request(
        this.namespace,
        unarchive ? 'unarchive' : 'archive',
        (error, { data }) => resolve(Boolean(data)),
        { id },
        Client.methods.PUT
      );
    });
  }

  enable(id) {
    return this.toggleEnabled(id);
  }

  disable(id) {
    return this.toggleEnabled(id, true);
  }

  toggleEnabled(id, disabled) {
    return new Promise((resolve, reject) => {
      if (!id) throw new Error('provide_id');

      this.client.request(
        this.namespace,
        disabled ? 'disable' : 'enable',
        (error, { data }) => resolve(Boolean(data)),
        { id },
        Client.methods.PUT
      );
    });
  }
}
